import { useEffect, useState } from 'react';
import { getApiErrorMessage } from './apiErrorMessage';
import novaOpsApi from '../../lib/novaOpsApi';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { Field, FieldGroup, FieldLabel } from '../../components/ui/field';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';

interface WalletTransaction {
  id: string;
  occurredAt: string;
  type: string;
  direction: 'credit' | 'debit';
  amount: string;
  currency: string;
  reference: string | null;
  status: string;
}
interface WalletHistoryPage {
  items: WalletTransaction[];
  nextCursor: number | null;
  hasNext: boolean;
}
interface Props {
  selector: string;
  wallets: Array<{ walletAccountNumber: string; name: string | null }>;
}

export function WalletHistoryPanel({ selector, wallets }: Props) {
  const [selectedWallet, setSelectedWallet] = useState('');
  const walletNumber = wallets.some(
    (wallet) => wallet.walletAccountNumber === selectedWallet,
  )
    ? selectedWallet
    : wallets[0]?.walletAccountNumber;
  const [dateOrder, setDateOrder] = useState('source');
  const [direction, setDirection] = useState('all');
  const [type, setType] = useState('');
  const [draftType, setDraftType] = useState('');
  const [draftSearch, setDraftSearch] = useState('');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [cursors, setCursors] = useState<number[]>([0]);
  const cursor = cursors[cursors.length - 1];
  const [page, setPage] = useState<WalletHistoryPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const invalidRange = Boolean(fromDate && toDate && fromDate > toDate);

  useEffect(() => {
    if (!walletNumber || invalidRange) return;
    const controller = new AbortController();
    const params = new URLSearchParams(selector);
    params.set('wallet_account_number', walletNumber);
    params.set('limit', '50');
    params.set('cursor', String(cursor));
    if (dateOrder !== 'source') params.set('date_order', dateOrder);
    if (direction !== 'all') params.set('direction', direction);
    if (type.trim()) params.set('type', type.trim());
    if (search.trim()) params.set('search', search.trim());
    if (fromDate) params.set('fromDate', fromDate);
    if (toDate) params.set('toDate', toDate);
    setLoading(true);
    setError('');
    setPage(null);
    void novaOpsApi
      .get<WalletHistoryPage>(`/ops/customers/wallet-transactions?${params}`, {
        signal: controller.signal,
      })
      .then(({ data }) => {
        if (!controller.signal.aborted) setPage(data);
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setError(
            getApiErrorMessage(
              error,
              'Wallet transaction history is unavailable. Try refreshing.',
            ),
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [
    selector,
    walletNumber,
    cursor,
    fromDate,
    toDate,
    invalidRange,
    refresh,
    dateOrder,
    direction,
    type,
    search,
  ]);

  if (!walletNumber) return null;

  return (
    <section
      className="mt-6 flex flex-col gap-4 border-t pt-6"
      aria-label="Wallet transaction history"
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <h4 className="text-base font-semibold">Transaction history</h4>
          <p className="text-sm text-muted-foreground">
            Complete wallet ledger, including credits, transfers, fees and
            withdrawals. All time by default. Filters apply across the ledger.
            Dates and times are UTC.
          </p>
        </div>
        <Button
          variant="outline"
          disabled={loading}
          onClick={() => {
            setCursors([0]);
            setRefresh((value) => value + 1);
          }}
        >
          Refresh history
        </Button>
      </div>
      <FieldGroup className="md:grid-cols-3">
        <Field>
          <FieldLabel htmlFor="wallet-history-account">
            Wallet account
          </FieldLabel>
          {wallets.length === 1 ? (
            <p id="wallet-history-account" className="font-mono text-sm">
              {walletNumber}
            </p>
          ) : (
            <Select
              value={walletNumber}
              onValueChange={(value) => {
                setSelectedWallet(value ?? '');
                setCursors([0]);
              }}
            >
              <SelectTrigger id="wallet-history-account">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {wallets.map((wallet) => (
                    <SelectItem
                      key={wallet.walletAccountNumber}
                      value={wallet.walletAccountNumber}
                    >
                      {wallet.name || 'Wallet'} — {wallet.walletAccountNumber}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          )}
        </Field>
        <Field data-invalid={invalidRange || undefined}>
          <FieldLabel htmlFor="wallet-history-from">From date (UTC)</FieldLabel>
          <Input
            id="wallet-history-from"
            type="date"
            value={fromDate}
            aria-invalid={invalidRange}
            onChange={(event) => {
              setFromDate(event.target.value);
              setCursors([0]);
            }}
          />
        </Field>
        <Field data-invalid={invalidRange || undefined}>
          <FieldLabel htmlFor="wallet-history-to">To date (UTC)</FieldLabel>
          <Input
            id="wallet-history-to"
            type="date"
            value={toDate}
            aria-invalid={invalidRange}
            onChange={(event) => {
              setToDate(event.target.value);
              setCursors([0]);
            }}
          />
        </Field>
      </FieldGroup>
      <FieldGroup className="md:grid-cols-4">
        <Field>
          <FieldLabel htmlFor="wallet-history-order">Date order</FieldLabel>
          <Select
            value={dateOrder}
            onValueChange={(value) => {
              setDateOrder(value ?? 'source');
              setCursors([0]);
            }}
          >
            <SelectTrigger id="wallet-history-order">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="source">Source order</SelectItem>
                <SelectItem value="desc">Newest first (DESC)</SelectItem>
                <SelectItem value="asc">Oldest first (ASC)</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </Field>
        <Field>
          <FieldLabel htmlFor="wallet-history-direction">Direction</FieldLabel>
          <Select
            value={direction}
            onValueChange={(value) => {
              setDirection(value ?? 'all');
              setCursors([0]);
            }}
          >
            <SelectTrigger id="wallet-history-direction">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="all">All directions</SelectItem>
                <SelectItem value="credit">Credit</SelectItem>
                <SelectItem value="debit">Debit</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </Field>
        <Field>
          <FieldLabel htmlFor="wallet-history-type">Type (exact)</FieldLabel>
          <Input
            id="wallet-history-type"
            placeholder="All types"
            maxLength={100}
            value={draftType}
            onChange={(event) => setDraftType(event.target.value)}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="wallet-history-search">Search ledger</FieldLabel>
          <Input
            id="wallet-history-search"
            placeholder="Reference, status or transaction ID"
            maxLength={200}
            value={draftSearch}
            onChange={(event) => setDraftSearch(event.target.value)}
          />
        </Field>
      </FieldGroup>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={() => {
            setType(draftType);
            setSearch(draftSearch);
            setCursors([0]);
          }}
        >
          Apply text filters
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            setFromDate('');
            setToDate('');
            setDateOrder('source');
            setDirection('all');
            setType('');
            setSearch('');
            setDraftType('');
            setDraftSearch('');
            setCursors([0]);
          }}
        >
          Clear filters
        </Button>
      </div>
      {dateOrder !== 'source' && (
        <p className="text-sm text-muted-foreground">
          Date order applies to the complete matching ledger, up to 10,000
          source entries. Larger wallets remain available in source order.
        </p>
      )}
      {invalidRange ? (
        <Alert variant="destructive">
          <AlertDescription>
            From date must be on or before To date.
          </AlertDescription>
        </Alert>
      ) : error ? (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : loading ? (
        <p role="status">Loading wallet history…</p>
      ) : (
        page && (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date / time (UTC)</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Direction</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Transaction ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {page.items.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="whitespace-nowrap">
                      {new Date(entry.occurredAt).toLocaleString('en-ZA', {
                        timeZone: 'UTC',
                        hour12: false,
                      })}
                    </TableCell>
                    <TableCell>{entry.type}</TableCell>
                    <TableCell>{entry.direction}</TableCell>
                    <TableCell>{entry.reference || '—'}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {entry.currency} {entry.amount}
                    </TableCell>
                    <TableCell>{entry.status}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {entry.id}
                    </TableCell>
                  </TableRow>
                ))}
                {page.items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      {page.hasNext
                        ? 'No matching entries on this page. Continue to the next page.'
                        : 'No wallet ledger entries found for the selected dates.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                Page {cursors.length} · {page.items.length} entries on this page
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  disabled={cursors.length === 1}
                  onClick={() => setCursors((values) => values.slice(0, -1))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  disabled={!page.hasNext || page.nextCursor === null}
                  onClick={() => {
                    if (page.nextCursor !== null)
                      setCursors((values) => [...values, page.nextCursor!]);
                  }}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )
      )}
    </section>
  );
}
